import {toNumberCurrency, formatStorePrice} from '@/utils/ecommerce/currency.js';
import {isVatUndefined, isVatActive} from '@/utils/ecommerce/vat.js';

/**
 * Maps the raw productPricingApi response into the normalized shape
 * produced by PPdata.getPPModel() / PPdata.createDataModel().
 *
 * Mirrors the structure of cart-line-item-mapper.js.
 *
 * NOTE: cartInfo (PPdata.createCartModel) reads document.documentElement.lang,
 * localStorage, and browser cookies. Build and merge it separately in the
 * consuming component or Redux middleware — it is intentionally excluded here.
 */

// ---------------------------------------------------------------------------
// Key-normalization helpers (mirror PPdata.parseProductString / nomrmalizeProductKeys)
// ---------------------------------------------------------------------------

/**
 * Parses a brace-delimited product string (e.g. "{key=value, key2=value2}") into a plain object.
 *
 * @param {string} prodString
 * @returns {Object}
 */
function parseProductString(prodString) {
  const parsedProd = {};
  const cleaned = prodString.replaceAll(/[{}]/gu, '');
  cleaned.split(', ').forEach((pair) => {
    const parts = pair.split('=');
    parsedProd[parts[0]] = parts.slice(1).join('=');
  });
  return parsedProd;
}

/**
 * Strips numeric suffixes from numbered product keys (product1Title → productTitle, etc.).
 *
 * @param {Object} productObj
 * @returns {Object}
 */
function normalizeProductKeys(productObj) {
  const product = {...productObj};
  for (const key in product) {
    // Matches: product1, product1Title, noPriceLinkProduct1, noPriceLabelProduct1, …
    const productRegex = /([pP])roduct\d{1,2}/;
    if (productRegex.test(key)) {
      const newKey = key.replaceAll(/([pP])roduct\d{1,2}/g, '$1roduct');
      product[newKey] = product[key];
      delete product[key];
    }

    const moreInfoRegex = /addMoreInfo\d{1,2}/g;
    if (moreInfoRegex.exec(key)) {
      const newKey = key.replaceAll(/addMoreInfo\d{1,2}/g, 'addMoreInfo');
      product[newKey] = product[key];
      delete product[key];
    }
  }
  return product;
}

// ---------------------------------------------------------------------------
// Currency helper
// ---------------------------------------------------------------------------

/**
 * Extracts and normalizes currency metadata from the raw response.
 *
 * @param {Object} rawResponse
 * @returns {{ currencyCode: string, currencyPlacement: string, currencySymbol: string }}
 */
function mapCurrency(rawResponse) {
  return {
    currencyCode: `${rawResponse.currency}`,
    currencyPlacement: rawResponse.currencyPlacement ? `${rawResponse.currencyPlacement}` : 'before',
    currencySymbol: `${rawResponse.currencySymbol}`,
  };
}

// ---------------------------------------------------------------------------
// Locale helper
// ---------------------------------------------------------------------------

/**
 * Extracts and normalizes locale/store metadata from the raw response.
 *
 * @param {Object} rawResponse
 * @returns {{
 *   currency: string,
 *   locale: string,
 *   isOmni: boolean,
 *   storeDomain: string,
 *   formattedLocale: string,
 *   zone: string,
 *   currencySymbol: string,
 *   currencyPlacement: string,
 *   distributorNumber: string,
 *   paymentOptionAssets: Array
 * }}
 */
function mapLocale(rawResponse) {
  return {
    currency: rawResponse.currency ?? '',
    locale: rawResponse.locale ?? '',
    isOmni: Boolean(rawResponse.isOmni),
    storeDomain: rawResponse.storeDomain ?? '',
    formattedLocale: rawResponse.formattedLocale ?? '',
    zone: rawResponse.zone ?? '',
    currencySymbol: rawResponse.currencySymbol ?? '',
    currencyPlacement: rawResponse.currencyPlacement ?? 'before',
    distributorNumber: rawResponse.distributorNumber ?? '',
    paymentOptionAssets: Array.isArray(rawResponse.paymentOptionAssets)
      ? rawResponse.paymentOptionAssets
      : [],
  };
}

// ---------------------------------------------------------------------------
// Product-item extraction (mirrors the productKeys loop in PPdata.createDataModel)
// ---------------------------------------------------------------------------

/**
 * Extracts ordered product sub-items (product1, product2, …) from a raw product data object.
 *
 * @param {Object} rawProductData
 * @returns {Object[]} Key-normalized product items ready for mapProduct
 */
function extractProductItems(rawProductData) {
  const productKeys = Object.keys(rawProductData)
    .sort((a, b) => a.localeCompare(b))
    .filter((key) => /^product\d{1,2}$/.exec(key));

  return productKeys.reduce((acc, key) => {
    if (typeof rawProductData[key] === 'string' && rawProductData[key].length) {
      acc.push(normalizeProductKeys(parseProductString(rawProductData[key])));
    } else if (rawProductData[key] && typeof rawProductData[key] === 'object') {
      const productObj = {
        ...rawProductData[key],
        ...(rawProductData[`${key}Icon`] && {productIcon: rawProductData[`${key}Icon`]}),
      };
      acc.push(normalizeProductKeys(productObj));
    }
    return acc;
  }, []);
}

/**
 * Filters out empty/invalid product items and those with no purchasable signal.
 * Mirrors the .filter() applied to hasProducts in PPdata.createDataModel.
 *
 * @param {Object[]} products
 * @returns {Object[]}
 */
function filterValidProducts(products) {
  return products.filter((prod) => {
    if (!prod || Object.keys(prod).length === 0) {
      console.error('no product issue!', prod);
      return false;
    }
    return !!prod.productSku || !!prod.noPriceLabel || prod.isFree?.toString() === 'true';
  });
}

// ---------------------------------------------------------------------------
// Product-level mappers
// ---------------------------------------------------------------------------

/**
 * Normalizes a purchasable product entry (isPurchasable == 'true').
 *
 * @param {Object} prod - Key-normalized product object
 * @param {Object} currency - Output of mapCurrency
 * @param {Object} rawResponse - Full raw API response (provides formattedLocale)
 * @returns {Object|null} Normalized purchasable product, or null when amount is missing
 */
function mapPurchasableProduct(prod, currency, rawResponse) {
  if (!prod.amount) {
    console.error('ERROR: Purchasable product with no price', prod);
    return null;
  }

  // NOTE (deferred): isVatActive reads browser cookies at call time. Inject a vatActive boolean
  // via options if this mapper is ever called outside a browser context (SSR, unit tests).
  const isVat = !isVatUndefined() && isVatActive();
  const rawAmount = isVat && prod.vatAmount ? prod.vatAmount : prod.amount;
  const amount = toNumberCurrency(rawAmount);
  const productSku = prod.productSku;

  return {
    isPurchasable: true,
    amount,
    __amount__: toNumberCurrency(prod.amount),
    __vatAmount__: toNumberCurrency(prod.vatAmount),
    displayPrice: formatStorePrice(amount, rawResponse.formattedLocale, currency),
    productTitle: prod.productTitle,
    renewalTitle: prod.renewalTermProduct || null,
    tag: prod.productLicense,
    modalIdentifier: prod.modalIdentifierDropdown || null,
    currency: prod.currency,
    _id: prod._id,
    qty: Number.isNaN(Number(prod.productQuantity)) ? null : Number(prod.productQuantity),
    qtyFrom: Number(prod.qtyFrom),
    qtyTo: Number(prod.qtyTo),
    sku: productSku,
    ...(prod.productIcon && {productIcon: prod.productIcon}),
    dataLayerSku: productSku,
    moreInfo: prod.addMoreInfo || null,
    noPriceLabel: prod.noPriceLabelProduct || null,
    noPriceLink: prod.noPriceLinkProduct || null,
  };
}

/**
 * Normalizes a non-purchasable product entry.
 *
 * @param {Object} prod - Key-normalized product object
 * @returns {Object}
 */
function mapNonPurchasableProduct(prod) {
  const productSku = prod.productSku;
  return {
    isPurchasable: false,
    isFree: prod.isFree,
    productTitle: prod.productTitle,
    tag: prod.productLicense,
    modalIdentifier: prod.modalIdentifierDropdown || null,
    _id: prod._id || crypto.randomUUID(),
    sku: productSku || crypto.randomUUID(),
    ...(prod.productIcon && {productIcon: prod.productIcon}),
    dataLayerSku: productSku,
    moreInfo: prod.addMoreInfo || null,
    noPriceLabel: prod.noPriceLabelProduct || null,
    noPriceLink: prod.noPriceLinkProduct || null,
  };
}

/**
 * Routes a key-normalized product to the correct normalizer based on purchasability.
 * Mirrors the .map() callback inside PPdata.createDataModel.
 *
 * @param {Object} prod
 * @param {Object} currency
 * @param {Object} rawResponse
 * @returns {Object|null}
 */
function mapProduct(prod, currency, rawResponse) {
  const normalizedProd = {...prod, isFree: prod.isFree || false};

  // NOTE (deferred): geoblocking reads globalThis.storeVariables at call time. Inject a boolean
  // via options if environment-agnostic behavior (SSR, tests) is needed.
  if (globalThis.storeVariables?.geoblockingStatus === 'enabled') {
    normalizedProd.isPurchasable = 'false';
  }

  return normalizedProd.isPurchasable == 'true'
    ? mapPurchasableProduct(normalizedProd, currency, rawResponse)
    : mapNonPurchasableProduct(normalizedProd);
}

// ---------------------------------------------------------------------------
// Active SKU resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the initially active SKU for a product model.
 * Prefers the first purchasable product; falls back to the first product overall.
 *
 * @param {Object[]} products - Normalized products on the product model
 * @returns {{ activeSku: string, activePrice: number, activeTag: string }}
 */
function resolveActiveSku(products) {
  if (products[0]?.isPurchasable) {
    return {activeSku: products[0].sku, activePrice: products[0].amount, activeTag: products[0].tag};
  }

  for (const prod of products) {
    if (prod?.isPurchasable) {
      return {activeSku: prod.sku, activePrice: prod.amount, activeTag: prod.tag};
    }
  }

  return {activeSku: products[0].sku, activePrice: products[0].amount, activeTag: products[0].tag};
}

// ---------------------------------------------------------------------------
// Product model mapper
// ---------------------------------------------------------------------------

/**
 * Normalizes a single product model, including its nested products, active SKU,
 * and bundle expansion. Mirrors the productModel assembly in PPdata.createDataModel.
 *
 * @param {Object} rawProductData
 * @param {boolean} isBundle - Whether this product model should be expanded as a bundle
 * @param {Object} currency - Output of mapCurrency
 * @param {Object} rawResponse
 * @param {string} componentId
 * @param {string} ctId
 * @returns {Object|Object[]} Product model, or array of product models for bundles
 */
function mapProductModel(rawProductData, isBundle, currency, rawResponse, componentId, ctId) {
  const products = filterValidProducts(extractProductItems(rawProductData))
    .map((prod) => mapProduct(prod, currency, rawResponse))
    .filter(Boolean); // remove nulls from purchasable-with-no-price entries

  const productPath = rawProductData.path.replace(/(\/jcr:content\/data\/master)/, '');

  let productModel = {
    componentId,
    id: crypto.randomUUID(),
    title: rawProductData.productTitle || rawProductData.bundleTitle,
    tag: rawProductData.productTag || rawProductData.bundleTag,
    modalIdentifier: rawProductData.modalIdentifierDropdown || null,
    termMoreInfo: rawProductData.termMoreInfo || null,
    systemRequirementUrl: rawProductData.systemRequirementUrl || rawProductData.bundleSystemRequirementsLink,
    systemRequirementLabel: rawProductData.bundleSystemRequirementsLabel,
    languageSupportedUrl: rawProductData.languageSupportedUrl || rawProductData.bundleSupportedLanguagesLink,
    languageSupportedLabel: rawProductData.bundleSupportedLanguagesLabel,
    whatsIncluded: rawProductData.whatsIncluded,
    longDescription: rawProductData.longDescription || rawProductData.bundleLongDescription,
    bullets: [
      rawProductData.shortDescriptionBullet1 || rawProductData.bundleShortDescriptionBullet1,
      rawProductData.shortDescriptionBullet2 || rawProductData.bundleShortDescriptionBullet2,
      rawProductData.shortDescriptionBullet3 || rawProductData.bundleShortDescriptionBullet3,
    ],
    products,
    allRenewalTitle: rawProductData.renewalTerm,
    compare: false,
    productPath,
    icon: rawProductData.productIcon,
    ctID: ctId,
    inputError: {error: false},
    qty: 1,
    cartQty: {qty: 0, update: false},
  };

  if (products.length) {
    productModel.allSkus = products.reduce((acc, prod) => {
      acc[prod.sku] = productModel.id;
      return acc;
    }, {});
    Object.assign(productModel, resolveActiveSku(products));
  }

  if (isBundle) {
    return products.map((product) => {
      const qty = product.qty || 1;
      return {
        ...productModel,
        id: crypto.randomUUID(),
        icon: product.productIcon,
        activeSku: product.sku,
        activePrice: product.amount * qty,
        activeTag: product.tag,
        products: [{...product, renewalTitle: productModel.allRenewalTitle}],
        qty,
      };
    });
  }

  return productModel;
}

// ---------------------------------------------------------------------------
// Directory helper
// ---------------------------------------------------------------------------

/**
 * Maps a directory (nested array of product items) into product models.
 *
 * @param {Object[]} directoryItems - Non-empty product items from the directory
 * @param {boolean} isBundle
 * @param {Object} currency
 * @param {Object} rawResponse
 * @param {string} componentId
 * @param {string} ctId
 * @returns {(Object|Object[])[]}
 */
function mapDirectory(directoryItems, isBundle, currency, rawResponse, componentId, ctId) {
  return directoryItems.map((item) =>
    mapProductModel(item, isBundle, currency, rawResponse, componentId, ctId),
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Maps the raw /bin/esri/productpricing response into the normalized product pricing model.
 * Mirrors PPdata.getPPModel() and PPdata.createDataModel().
 *
 * @param {Object} rawResponse - Raw response from /bin/esri/productpricing
 * @param {Object} [options={}]
 * @param {string} [options.componentId] - AEM component ID
 * @param {string} [options.ctId]        - Comparison-table ID
 * @param {boolean} [options.isBundle]   - Whether all cards in this response are bundles
 * @returns {{ productModels: (Object|Object[])[] }}
 */
export function mapProductPricingResponse(rawResponse, options = {}) {
  const {componentId, ctId, isBundle = false} = options;

  if (!rawResponse?.data?.length) {
    throw new Error('No product data available, check selected content fragment');
  }

  const currency = mapCurrency(rawResponse);
  const locale = mapLocale(rawResponse);
  const prodData = rawResponse.data.filter((item) => item && Object.keys(item).length > 0);

  const productModels = prodData.map((item) => {
    // Detect directory shape: { folderName: [[...products]] }
    const folderName = Object.keys(item)[0];
    const maybeDirectory = item[folderName];

    if (maybeDirectory && Array.isArray(maybeDirectory)) {
      const directoryItems = maybeDirectory[0].filter(
        (prod) => prod && Object.keys(prod).length > 0,
      );
      if (directoryItems.length) {
        return mapDirectory(directoryItems, isBundle, currency, rawResponse, componentId, ctId);
      }
    }

    return mapProductModel(item, isBundle, currency, rawResponse, componentId, ctId);
  });

  return {productModels, locale};
}
